import {
  getNestedOrFloatingElementById,
  getNestedElementById,
  getNestedSubelementsByType,
  batchCreateNameSuper,
  fieldToElementTypeMap,
  getNextUuid,
} from '@fs/zion-sls'
import { getSuperEl } from '../../../../../views/indexing/components/editPanels/sharedField/SharedFieldUtils'
import { removeConflictingElementIfExists } from './linkingDispatchHelpers'

/**
 * Finds existing NAME element on person or prepares dispatches to create one
 *
 * @param {Object} options
 * @param {Object} options.person - Target person
 * @param {string} options.partitionKey - Partition key
 * @returns {Object} { nameElement, nameElementId, dispatches, isNew }
 */
export const findOrCreateNameElement = ({ person }) => {
  const existingNameEl = getNestedSubelementsByType(person, 'NAME')?.[0]
  const nameElementId = existingNameEl?.id || getNextUuid()

  if (existingNameEl) {
    return {
      nameElement: existingNameEl,
      nameElementId,
      dispatches: [],
      isNew: false,
    }
  }

  // Create new NAME element
  const dispatches = batchCreateNameSuper({
    partitionKey: person?.partitionKey,
    nameElementId,
    nameType: 'BIRTH_NAME',
    personId: person?.id,
  })

  return {
    nameElement: null, // Will be created
    nameElementId,
    dispatches,
    isNew: true,
  }
}

/**
 * Creates intermediate element (NAME_SURNAME, NAME_GIVEN, etc.) and links it to parent NAME element
 *
 * @param {Object} options
 * @param {string} options.nameElementId - ID of parent NAME element
 * @param {string} options.intermediateElementType - Type (e.g., 'NAME_SURNAME')
 * @param {string} options.partitionKey - Partition key
 * @param {Object} options.nameElement - Existing NAME element (null if new)
 * @param {string} options.personPartitionKey - Person's partition key
 * @returns {Object} { intermediateElementId, dispatches }
 */
export const createIntermediateElementWithLink = ({
  nameElementId,
  intermediateElementType,
  partitionKey,
  nameElement,
  personPartitionKey,
}) => {
  const intermediateElementId = getNextUuid()

  const dispatches = [
    // Create the intermediate element
    {
      type: 'CREATE_ELEMENT',
      value: {
        id: intermediateElementId,
        partitionKey,
        elementType: intermediateElementType,
      },
    },
    // Link it to the NAME element
    {
      type: 'ADD_SUB',
      value: {
        superPartitionKey: nameElement?.partitionKey || personPartitionKey,
        subPartitionKey: partitionKey,
        superId: nameElementId,
        subId: intermediateElementId,
        elementType: nameElement?.elementType || 'NAME',
      },
    },
  ]

  return {
    intermediateElementId,
    dispatches,
  }
}

/**
 * Links a shared name field to a person
 * Handles all cases: existing structure, needs intermediate, needs NAME element, conflict removal
 *
 * @param {Object} options
 * @param {Object} options.person - Target person
 * @param {Object} options.sharedElement - Shared field element
 * @param {string} options.partitionKey - Partition key
 * @param {Array} options.images - SLS images state
 * @returns {Array<Object>} All dispatches needed to complete the link
 */
export const linkSharedFieldToPerson = ({ person, sharedElement, partitionKey }) => {
  const dispatches = []

  // Check if person already has a common super element with the shared element
  const superEl = getSuperEl(person, sharedElement)

  if (!superEl) {
    // Person doesn't have the required structure - need to create it
    const desiredSuperElementType = fieldToElementTypeMap[sharedElement.fieldType] || 'NAME'

    // Check if person has an existing element that matches the desired super element type
    const matchingSuperElements = getNestedSubelementsByType(person, desiredSuperElementType)

    if (matchingSuperElements.length > 0) {
      // Person has a matching intermediate element (e.g., NAME_SURNAME)
      // We can add the shared field as a sub-element to it
      const superElement = matchingSuperElements[0]

      // Check for and remove conflicting element of same type
      const conflictRemovalDispatch = removeConflictingElementIfExists({
        person: superElement,
        sharedElement,
      })

      if (conflictRemovalDispatch) {
        // Update the dispatch to use the superElement instead of person
        conflictRemovalDispatch.value.superPartitionKey = superElement?.partitionKey
        conflictRemovalDispatch.value.superId = superElement.id
        conflictRemovalDispatch.value.elementType = superElement.elementType
        dispatches.push(conflictRemovalDispatch)
      }

      // Link the shared element to the matching super element
      dispatches.push({
        type: 'ADD_SUB',
        value: {
          superPartitionKey: superElement.partitionKey,
          subPartitionKey: sharedElement?.partitionKey,
          superId: superElement?.id,
          subId: sharedElement.id,
          elementType: superElement.elementType,
        },
      })
    } else {
      // Person doesn't have the intermediate element - need to create hierarchy
      // First, ensure person has a NAME element
      const { nameElementId, dispatches: nameDispatches } = findOrCreateNameElement({
        person,
        partitionKey,
      })
      dispatches.push(...nameDispatches)

      // Create the intermediate element (e.g., NAME_SURNAME)
      const { intermediateElementId, dispatches: intermediateDispatches } = createIntermediateElementWithLink({
        nameElementId,
        intermediateElementType: desiredSuperElementType,
        partitionKey,
        nameElement: getNestedSubelementsByType(person, 'NAME')?.[0],
        personPartitionKey: person?.partitionKey,
      })
      dispatches.push(...intermediateDispatches)

      // Link the shared field to the new intermediate element
      dispatches.push({
        type: 'ADD_SUB',
        value: {
          superPartitionKey: partitionKey || person?.partitionKey,
          subPartitionKey: sharedElement?.partitionKey || partitionKey,
          superId: intermediateElementId,
          subId: sharedElement.id,
          elementType: desiredSuperElementType,
        },
      })
    }
  }

  return dispatches
}

/**
 * Unlinks a shared field from a person
 * Determines correct removal based on field-level vs intermediate-level sharing
 *
 * @param {Object} options
 * @param {Object} options.person - Person to unlink from
 * @param {Object} options.sharedElement - Shared element
 * @param {string} options.partitionKey - Partition key
 * @param {Array} options.images - SLS images state
 * @returns {Array<Object>} Unlink dispatches
 */
export const unlinkSharedFieldFromPerson = ({ person, sharedElement, partitionKey, images }) => {
  const dispatches = []

  if (!sharedElement?.superElements) return dispatches

  // Determine if the shared element is a field (leaf) or intermediate
  // Case 1: Shared element is a field (e.g., NAME_SURN "Bush")
  //   Hierarchy: Person → NAME → NAME_SURNAME (find this) → NAME_SURN (shared)
  // Case 2: Shared element is intermediate (e.g., NAME_SURNAME x2)
  //   Hierarchy: Person → NAME (find this) → NAME_SURNAME (shared) → NAME_SURN

  const isSharedElementField = sharedElement.elementType === 'FIELD'
  let superElement
  let subElement

  if (isSharedElementField) {
    // Case 1: Find the intermediate element (NAME_SURNAME, NAME_GIVEN, etc.)
    superElement = sharedElement.superElements
      .map((ref) => getNestedOrFloatingElementById(images, ref.id))
      .find((intermediate) => {
        return (
          intermediate && getNestedElementById([{ id: person.id, subElements: person.subElements }], intermediate.id)
        )
      })

    subElement = sharedElement

    if (!superElement) return dispatches
  } else {
    // Case 2: Shared element is the intermediate - find the NAME element
    superElement = sharedElement.superElements
      .map((ref) => getNestedOrFloatingElementById(images, ref.id))
      .find((nameElement) => {
        return nameElement && getNestedElementById([{ id: person.id, subElements: person.subElements }], nameElement.id)
      })

    subElement = sharedElement

    if (!superElement) return dispatches
  }

  // Remove the link
  dispatches.push({
    type: 'REM_SUB',
    value: {
      superPartitionKey: superElement.partitionKey || partitionKey,
      subPartitionKey: subElement.partitionKey || partitionKey,
      superId: superElement.id,
      subId: subElement.id,
      elementType: superElement.elementType,
    },
  })

  return dispatches
}
